import * as braintree from 'braintree-web';
import { tokens } from '@hotjar/hotsauce';
import { PaymentMethod, ValidationError } from '@hotjar/webapp/billing/payment';
import { getClientToken } from '../api/tokens';

let instance: braintree.HostedFields | null = null;

interface SetupHostedFieldsConfig {
  accountId: number | null | undefined;
  formId: string;
  onValidationError?(invalidFieldKeys: Array<string>): void;
  onCardValidChange?(cardType: { type: string | null; accepted: boolean }): void;
}

interface TokenizeCardOptions {
  cardholderName: string;
  city: string;
  postalCode: string;
  countryCode: string;
}

function getInvalidFields(
  fields: braintree.HostedFieldsFieldDataFields | Record<string, never>,
): string[] {
  return Object.keys(fields)
    .filter((field) => {
      const { isValid, isEmpty } = fields[field as braintree.HostedFieldsHostedFieldsFieldName];

      return !isValid || isEmpty;
    })
    .map((id) => id);
}

// List of all available types can be found here
// https://braintree.github.io/braintree-web/current/HostedFields.html#~hostedFieldsCard
// This is a subset of card types that we currently support in our payment system
const SUPPORTED_CARDS_BRAINTREE_IDS = [
  'american-express',
  'visa',
  'master-card',
  'maestro',
  'discover',
];

export function setFocus(field: braintree.HostedFieldsHostedFieldsFieldName): void {
  instance?.focus(field);
}

export function checkHostedFormValidity(): { isValid: boolean; invalidFields: string[] } {
  const state = instance?.getState();
  const invalidFields = getInvalidFields(state ? state.fields : {});
  return {
    isValid: !invalidFields.length,
    invalidFields,
  };
}

export async function setupHostedFields({
  accountId,
  formId,
  onValidationError,
  onCardValidChange,
}: SetupHostedFieldsConfig): Promise<braintree.HostedFields> {
  const { token } = await getClientToken(accountId as number);

  instance = await braintree.hostedFields.create({
    authorization: token,
    styles: {
      input: {
        'font-size': '14px',
        color: tokens.color.text_default,
      },
      '::-webkit-input-placeholder': {
        color: tokens.color.text_disabled,
      },
      ':-moz-placeholder': {
        color: tokens.color.text_disabled,
      },
      '::-moz-placeholder': {
        color: tokens.color.text_disabled,
      },
      ':-ms-input-placeholder': {
        color: tokens.color.text_disabled,
      },
    },
    fields: {
      number: {
        selector: `#${formId}-number`,
      },
      cvv: {
        selector: `#${formId}-cvv`,
      },
      expirationMonth: {
        selector: `#${formId}-expiration-month`,
        placeholder: 'MM',
      },
      expirationYear: {
        selector: `#${formId}-expiration-year`,
        placeholder: 'YYYY',
      },
    },
  });

  // listen to validation changes and return a list of invalid fields by key names
  instance.on('validityChange', (event) => {
    const invalidList = getInvalidFields(event.fields);
    onValidationError?.(invalidList);
  });

  instance.on('cardTypeChange', (event) => {
    const acceptedCard = event.cards.filter(({ type }) =>
      SUPPORTED_CARDS_BRAINTREE_IDS.includes(type),
    )[0];

    if (acceptedCard) {
      onCardValidChange?.({
        type: acceptedCard.niceType,
        accepted: true,
      });
    } else if (event?.cards?.length) {
      onCardValidChange?.({
        type: event.cards[0].niceType,
        accepted: false,
      });
    } else {
      onCardValidChange?.({
        type: null,
        accepted: true,
      });
    }
  });

  return instance;
}

export async function tokenizeCard({
  cardholderName,
  city,
  countryCode,
  postalCode,
}: TokenizeCardOptions): Promise<PaymentMethod> {
  if (!instance) {
    throw new Error('submit: no hosted field instance set');
  }

  try {
    const { nonce, details } = await instance.tokenize({
      cardholderName,
      billingAddress: {
        locality: city,
        postalCode,
        countryCodeAlpha2: countryCode,
      },
    });

    return new PaymentMethod(
      nonce,
      details.bin,
      'credit_card',
      cardholderName,
      countryCode,
      city,
      postalCode,
    );
  } catch ({ code, details }) {
    let errorFields = [];

    if (code === 'HOSTED_FIELDS_FIELDS_INVALID') {
      // @ts-expect-error fix catch error type
      errorFields = details.invalidFieldKeys;
    } else if (code === 'HOSTED_FIELDS_FIELDS_EMPTY') {
      errorFields = ['number', 'cvv', 'expirationMonth', 'expirationYear'];
    }

    throw new ValidationError('Invalid Card', errorFields);
  }
}
